import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Plus, Search, Filter, Wrench, Paintbrush, Zap, Droplets, Building, Settings, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
interface ManutencaoReforma {
  id: string
  tipo: 'pintura' | 'eletrica' | 'hidraulica' | 'estrutural' | 'limpeza' | 'jardinagem' | 'equipamentos' | 'outros'
  descricao: string
  valor: number
  dataInicio: string
  dataPrevista: string
  responsavel: string
  observacoes?: string
  status: 'concluido' | 'em_andamento' | 'planejado' | 'em_atraso'
  prioridade: 'baixa' | 'media' | 'alta' | 'urgente'
}

const tiposManutencao = [
  { value: 'pintura', label: 'Pintura', icon: Paintbrush },
  { value: 'eletrica', label: 'Elétrica', icon: Zap },
  { value: 'hidraulica', label: 'Hidráulica', icon: Droplets },
  { value: 'estrutural', label: 'Estrutural', icon: Building },
  { value: 'limpeza', label: 'Limpeza Profunda', icon: Settings },
  { value: 'jardinagem', label: 'Jardinagem', icon: Settings },
  { value: 'equipamentos', label: 'Equipamentos', icon: Settings },
  { value: 'outros', label: 'Outros', icon: Wrench }
]

const statusColors = {
  concluido: 'bg-green-500',
  em_andamento: 'bg-blue-500',
  planejado: 'bg-yellow-500',
  em_atraso: 'bg-red-500'
}

const statusLabels = {
  concluido: 'Concluído',
  em_andamento: 'Em Andamento',
  planejado: 'Planejado',
  em_atraso: 'Em Atraso'
}

const prioridadeColors = {
  baixa: 'bg-gray-500',
  media: 'bg-yellow-500',
  alta: 'bg-orange-500',
  urgente: 'bg-red-500'
}

const prioridadeLabels = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
  urgente: 'Urgente'
}

// Mock data
const mockManutencoes: ManutencaoReforma[] = [
  {
    id: '1',
    tipo: 'pintura',
    descricao: 'Pintura da fachada principal',
    valor: 2500.00,
    dataInicio: '2024-01-10',
    dataPrevista: '2024-01-15',
    responsavel: 'João Silva',
    status: 'em_andamento',
    prioridade: 'alta',
    observacoes: 'Incluir primer e duas demãos de tinta'
  },
  {
    id: '2',
    tipo: 'eletrica',
    descricao: 'Troca de lâmpadas do salão principal',
    valor: 350.00,
    dataInicio: '2024-01-18',
    dataPrevista: '2024-01-20',
    responsavel: 'Pedro Costa',
    status: 'concluido',
    prioridade: 'media'
  },
  {
    id: '3',
    tipo: 'hidraulica',
    descricao: 'Reparo no encanamento do banheiro',
    valor: 180.00,
    dataInicio: '2024-01-22',
    dataPrevista: '2024-01-25',
    responsavel: 'Maria Santos',
    status: 'planejado',
    prioridade: 'urgente'
  }
]

export function ManutencaoReformaPage() {
  const navigate = useNavigate()
  const [manutencoes, setManutencoes] = useState<ManutencaoReforma[]>(mockManutencoes)
  const [filtros, setFiltros] = useState({
    busca: '',
    tipo: '',
    status: '',
    prioridade: '',
    dataInicio: '',
    dataFim: ''
  })

  // Função para verificar se está em atraso
  const verificarAtraso = (manutencao: ManutencaoReforma): ManutencaoReforma => {
    const hoje = new Date()
    const dataPrevista = new Date(manutencao.dataPrevista)
    
    if (manutencao.status !== 'concluido' && dataPrevista < hoje) {
      return { ...manutencao, status: 'em_atraso' }
    }
    return manutencao
  }

  // Função para alterar status
  const alterarStatus = (id: string) => {
    setManutencoes(prev => 
      prev.map(manutencao => {
        if (manutencao.id === id) {
          const statusOrder: ManutencaoReforma['status'][] = ['planejado', 'em_andamento', 'concluido', 'em_atraso']
          const currentIndex = statusOrder.indexOf(manutencao.status)
          const nextIndex = (currentIndex + 1) % statusOrder.length
          return { ...manutencao, status: statusOrder[nextIndex] }
        }
        return manutencao
      })
    )
  }

  // Aplicar verificação de atraso nas manutenções
  const manutencoesComAtraso = manutencoes.map(verificarAtraso)

  const manutencoesFiltradas = manutencoesComAtraso.filter(manutencao => {
    if (filtros.tipo && manutencao.tipo !== filtros.tipo) return false
    if (filtros.status && manutencao.status !== filtros.status) return false
    if (filtros.prioridade && manutencao.prioridade !== filtros.prioridade) return false
    if (filtros.busca && !manutencao.descricao.toLowerCase().includes(filtros.busca.toLowerCase()) && 
        !manutencao.responsavel.toLowerCase().includes(filtros.busca.toLowerCase())) return false
    
    if (filtros.dataInicio) {
      const dataInicio = new Date(filtros.dataInicio)
      const manutencaoData = new Date(manutencao.dataPrevista)
      if (manutencaoData < dataInicio) return false
    }
    
    if (filtros.dataFim) {
      const dataFim = new Date(filtros.dataFim)
      const manutencaoData = new Date(manutencao.dataPrevista)
      if (manutencaoData > dataFim) return false
    }
    
    return true
  })

  const totalInvestido = manutencoesFiltradas.reduce((sum, manutencao) => sum + manutencao.valor, 0)

  return (
    <div className="space-y-6 -mx-4 sm:-mx-6 lg:-mx-8 xl:-mx-12">
      <div className="px-4 sm:px-6 lg:px-8 xl:px-12">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/financas/administracao')}
              className="mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Wrench className="w-6 h-6 text-orange-500" />
                Manutenção e Reforma
              </h1>
              <p className="text-muted-foreground">
                Gestão de serviços de manutenção e reformas da igreja
              </p>
            </div>
          </div>
          <Button 
            className="bg-orange-600 hover:bg-orange-700"
            onClick={() => navigate('/financas/administracao/manutencao-reforma/cadastrar')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Cadastrar Serviço
          </Button>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card dark:bg-card border-border dark:border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-card-foreground dark:text-gray-400">Total Investido</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                R$ {totalInvestido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card dark:bg-card border-border dark:border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-card-foreground dark:text-gray-400">Concluídos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {manutencoesFiltradas.filter(m => m.status === 'concluido').length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card dark:bg-card border-border dark:border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-card-foreground dark:text-gray-400">Em Andamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {manutencoesFiltradas.filter(m => m.status === 'em_andamento').length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card dark:bg-card border-border dark:border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-card-foreground dark:text-gray-400">Planejados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {manutencoesFiltradas.filter(m => m.status === 'planejado').length}
              </div>
            </CardContent>
          </Card>
        </div>

      </div>

      {/* Filtros e Lista Unificados */}
      <Card className="bg-card dark:bg-card border-border dark:border-border mx-4 sm:mx-6 lg:mx-8 xl:mx-12">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-card-foreground dark:text-gray-400">
            <Filter className="w-4 h-4" />
            Serviços Cadastrados ({manutencoesFiltradas.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Seção de Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 bg-muted/20 rounded-lg">
            <div>
              <Label>Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por descrição ou responsável..."
                  value={filtros.busca}
                  onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={filtros.tipo} onValueChange={(value) => setFiltros({...filtros, tipo: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {tiposManutencao.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={filtros.status} onValueChange={(value) => setFiltros({...filtros, status: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="planejado">Planejado</SelectItem>
                  <SelectItem value="em_atraso">Em Atraso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioridade</Label>
              <Select value={filtros.prioridade} onValueChange={(value) => setFiltros({...filtros, prioridade: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as prioridades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as prioridades</SelectItem>
                  <SelectItem value="baixa">Baixa</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data Início</Label>
              <Input
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
              />
            </div>
            <div>
              <Label>Data Fim</Label>
              <Input
                type="date"
                value={filtros.dataFim}
                onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
              />
            </div>
          </div>

          {/* Tabela de Serviços */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border dark:border-gray-700">
                  <th className="text-left p-4 font-medium text-muted-foreground dark:text-gray-400">Tipo</th>
                  <th className="text-left p-4 font-medium text-muted-foreground dark:text-gray-400">Descrição</th>
                  <th className="text-left p-4 font-medium text-muted-foreground dark:text-gray-400">Responsável</th>
                  <th className="text-left p-4 font-medium text-muted-foreground dark:text-gray-400">Valor</th>
                  <th className="text-left p-4 font-medium text-muted-foreground dark:text-gray-400">Data Prevista</th>
                  <th className="text-left p-4 font-medium text-muted-foreground dark:text-gray-400">Status</th>
                  <th className="text-left p-4 font-medium text-muted-foreground dark:text-gray-400">Prioridade</th>
                  <th className="text-left p-4 font-medium text-muted-foreground dark:text-gray-400">Ações</th>
                </tr>
              </thead>
              <tbody>
                {manutencoesFiltradas.map((manutencao) => {
                  const TipoIcon = tiposManutencao.find(t => t.value === manutencao.tipo)?.icon || Wrench
                  return (
                    <tr key={manutencao.id} className="border-b border-border dark:border-gray-700">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-orange-900/30 dark:bg-orange-900/30 rounded-lg">
                            <TipoIcon className="w-4 h-4 text-orange-400 dark:text-orange-400" />
                          </div>
                          <span className="text-sm text-card-foreground dark:text-gray-200">
                            {tiposManutencao.find(t => t.value === manutencao.tipo)?.label}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-card-foreground dark:text-gray-200">{manutencao.descricao}</div>
                        {manutencao.observacoes && (
                          <div className="text-xs text-muted-foreground dark:text-gray-500 mt-1">{manutencao.observacoes}</div>
                        )}
                      </td>
                      <td className="p-4 text-card-foreground dark:text-gray-200">{manutencao.responsavel}</td>
                      <td className="p-4">
                        <span className="font-bold text-orange-400 dark:text-orange-400">
                          R$ {manutencao.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="p-4 text-card-foreground dark:text-gray-200">
                        {new Date(manutencao.dataPrevista).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="p-4">
                        <Badge 
                          className={`${statusColors[manutencao.status]} text-white cursor-pointer relative group`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            alterarStatus(manutencao.id);
                          }}
                          title="Clique para mudar status"
                        >
                          {statusLabels[manutencao.status]}
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Clique para mudar status
                          </span>
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge className={`${prioridadeColors[manutencao.prioridade]} text-white`}>
                          {prioridadeLabels[manutencao.prioridade]}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {manutencoesFiltradas.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum serviço encontrado com os filtros aplicados.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}